/**
 * rear_camera_store — 후방 카메라 수신 여부와 "후진이 막힌 순간"을 나르는 경량 스토어.
 *
 * 후방 영상이 없으면 뒤를 전혀 볼 수 없는 상태로 후진하는 셈이라, 콕핏에서 R 명령
 * 자체를 내보내지 않는다(요청). 막았다는 사실은 운전자에게 즉시 보여야 하므로 시도
 * 시각을 함께 발행한다 — 차량 기어는 R 로 바뀌지 않으므로 기어 값만 보고 있으면
 * 경고를 띄울 계기가 영영 오지 않는다.
 *
 * 스토어로 둔 이유: 가용 여부의 출처(useCameraReceiverFps → AdRemoteClient)와 소비처
 * (직접제어 기어 명령, HUD 경고 모달)가 컴포넌트 트리에서 멀리 떨어져 있다.
 * 무거운 의존성(brain-proto/mqtt)을 끌어오지 않도록 별도 경량 모듈로 둔다.
 */
import { useSyncExternalStore } from "react";

interface RearCameraState {
  available: boolean;
  /** 마지막으로 후진 명령이 막힌 시각(ms). 없으면 null. */
  blockedAt: number | null;
}

let state: RearCameraState = { available: false, blockedAt: null };
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getState(): RearCameraState {
  return state;
}

export function setRearCameraAvailable(available: boolean): void {
  const next = Boolean(available);
  if (next === state.available) {
    return;
  }
  state = { ...state, available: next };
  emit();
}

export function isRearCameraAvailable(): boolean {
  return state.available;
}

/**
 * 후진 명령을 막았음을 알린다. 매번 시각이 갱신되므로, 여러 번 눌러도 그때마다
 * 경고가 다시 뜬다 — 한 번 무시했다고 다음 시도에서 침묵하면 안 된다.
 */
export function notifyReverseBlocked(atMs: number = Date.now()): void {
  state = { ...state, blockedAt: atMs };
  emit();
}

export function useRearCameraState(): RearCameraState {
  return useSyncExternalStore(subscribe, getState, getState);
}

/** 테스트용 — 모듈 상태를 비운다. */
export function resetRearCameraStoreForTest(): void {
  state = { available: false, blockedAt: null };
  emit();
}
